import { Module, VuexModule, Action, Mutation } from "vuex-module-decorators";
import { fmtMsg, MessageHelper, store, ServerExceptionCode, NotificationType } from "commonui";
import { IVersionGroupService, VersionGroupModel, VersionGroupService } from "@/services";
import { VersionGroupModule } from "@/modules/versiongroup";
import { EmptyGuid } from "@/util/constants";
import {
	LessonPlanVersionActionStatus,
	LessonPlanVersionStatus,
	MethodologyType,
	MethodologyTypeEnum
} from "@/util/enum";
import { getSelectedVersionGroups } from "@/util/func";
import { ILessonPlanVersionModel, sortLessonPlanVersions } from "@/models/lesson-plan";
import { ILessonPlanVersionService, LessonPlanVersionService } from "@/services/lesson-plan";
import { LessonPlanLocale } from "@/locales/localeid";

function formatLessonPlanVersion(
	lessonPlanVersions: ILessonPlanVersionModel[],
	versionGroups: VersionGroupModel[]
): ILessonPlanVersionModel[] {
	return lessonPlanVersions.map((lpv) => {
		const versionGroup: VersionGroupModel | undefined = versionGroups.find((vg) => vg.id === lpv.versionGroupId);
		lpv.versionGroupName = versionGroup ? versionGroup.name : "";
		lpv.versionGroupType = versionGroup ? versionGroup.curriculumType : undefined;
		return lpv;
	});
}

function showErrorMessage({ error_code, error_description }): boolean {
	const errorDescriptionPrefix = "Error:";
	const regionNames = error_description.length > 0 ? error_description[0].replace(errorDescriptionPrefix, "") : "";
	switch (error_code) {
		case ServerExceptionCode.TargetHasExistedException:
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(LessonPlanLocale.LessonPlanVersionNameExistsMessage)
			);
			return false;
		case ServerExceptionCode.LessonPlanVersionUsedException:
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(LessonPlanLocale.LessonPlanVersionRegionStatusErrorMessage, { regionNames })
			);
			return false;
		case ServerExceptionCode.LessonPlanVersionIsBeingUsedInContentTypeException:
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(LessonPlanLocale.LessonPlanVersionContentTypeStatusErrorMessage, {
					contentTypeNames: regionNames
				})
			);
			return false;
		default:
			return true;
	}
}

@Module({ namespaced: true, name: "LessonPlanVersiState", store: store, dynamic: true })
export default class LessonPlanVersionState extends VuexModule {
	versionGroupService: IVersionGroupService = VersionGroupService();
	lessonPlanVersionService: ILessonPlanVersionService = LessonPlanVersionService();
	// versionGroups: VersionGroupModel[] = [];
	lessonPlanVersions: ILessonPlanVersionModel[] = [];
	currentVersionGroups: VersionGroupModel[] = [];
	currentVersionGroup: VersionGroupModel = {} as VersionGroupModel;
	currentMethodology: MethodologyType | MethodologyTypeEnum = MethodologyTypeEnum.All;
	currentLessonPlanVersion: ILessonPlanVersionModel = {
		methodology: MethodologyType.Classic
	} as ILessonPlanVersionModel;
	loading: boolean = false;
	canPublishedForREP: boolean = true;

	//
	// 	@Mutation
	// 	setVersionGroups(versionGroups: VersionGroupModel[]): void {
	// 		this.versionGroups = versionGroups;
	// 	}

	@Mutation
	async setSelectedVersionGroups(selectedVersionGroups: VersionGroupModel[]): Promise<void> {
		this.currentVersionGroups = selectedVersionGroups;
	}

	@Mutation
	async setVersionGroup(versionGroup: VersionGroupModel): Promise<void> {
		this.currentVersionGroup = versionGroup;
	}

	@Mutation
	async setMethodology(methodology: MethodologyType | MethodologyTypeEnum): Promise<void> {
		this.currentMethodology = methodology;
	}

	@Mutation
	setLessonPlanVersions(lessonPlanVersions: ILessonPlanVersionModel[]): void {
		this.lessonPlanVersions = lessonPlanVersions;
	}

	@Mutation
	setCurrentLessonPlanVersion(lessonPlanVersion: ILessonPlanVersionModel): void {
		this.currentLessonPlanVersion = lessonPlanVersion;
	}

	@Mutation
	SetIsMatched(isMatched: boolean): void {
		this.canPublishedForREP = isMatched;
	}

	@Action
	async IsTenLessonsForDiffUnitsREP(lessonPlanVersionId: string): Promise<void> {
		this.setLoading(true);
		const isMatched = await this.lessonPlanVersionService.IsTenLessonsForDiffUnitsREP(lessonPlanVersionId);
		this.SetIsMatched(isMatched);
		this.setLoading(false);
	}

	@Action
	async fetchLessonPlanVersions(): Promise<void> {
		this.setLoading(true);

		const versionGroups: VersionGroupModel[] = VersionGroupModule.versionGroups;
		const { selectedGSVersion, selectedLSVersion } =
			VersionGroupModule.selectedGsVersion === "-1" && VersionGroupModule.selectedLsVersion === "-1"
				? await this.versionGroupService.getVcsSettings()
				: {
						selectedGSVersion: VersionGroupModule.selectedGsVersion,
						selectedLSVersion: VersionGroupModule.selectedLsVersion
				  };
		const selectedVersionGroups: VersionGroupModel[] = getSelectedVersionGroups(
			versionGroups,
			selectedGSVersion,
			selectedLSVersion
		);
		const lessonPlanVersions: ILessonPlanVersionModel[] =
			selectedVersionGroups.length === 0
				? []
				: await this.lessonPlanVersionService.getLessonPlanVersions(
						selectedVersionGroups.map((svg) => svg.id),
						this.currentMethodology
				  );

		this.setSelectedVersionGroups(selectedVersionGroups);
		this.setLessonPlanVersions(
			formatLessonPlanVersion(lessonPlanVersions, versionGroups).sort(sortLessonPlanVersions)
		);

		const selectedVersionGroup: VersionGroupModel =
			selectedVersionGroups.length > 0 ? selectedVersionGroups[0] : ({} as VersionGroupModel);
		this.setVersionGroup(selectedVersionGroup);

		this.setLoading(false);
	}

	@Action
	async fetchLessonPlanVersion({
		versionId,
		action
	}: {
		versionId: string;
		action: LessonPlanVersionActionStatus;
	}): Promise<void> {
		this.setLoading(true);
		const lessonPlanVersion: ILessonPlanVersionModel = await this.lessonPlanVersionService.getLessonPlanVersion(
			versionId
		);
		if (action === LessonPlanVersionActionStatus.Duplicate) {
			lessonPlanVersion.name = `Duplicated from ${lessonPlanVersion.name}`;
		}
		this.setCurrentLessonPlanVersion(lessonPlanVersion);
		this.setLoading(false);
	}

	@Action
	async refreshLessonPlanVersions(needSetLoading: boolean = true): Promise<void> {
		if (needSetLoading) {
			this.setLoading(true);
		}

		const versionGroups: VersionGroupModel[] = VersionGroupModule.versionGroups;
		const selectedVersionGroups: VersionGroupModel[] = getSelectedVersionGroups(
			versionGroups,
			EmptyGuid,
			EmptyGuid
		);
		const lessonPlanVersions: ILessonPlanVersionModel[] =
			selectedVersionGroups.length === 0
				? []
				: await this.lessonPlanVersionService.getLessonPlanVersions(
						selectedVersionGroups.map((svg) => svg.id),
						this.currentMethodology
				  );

		this.setSelectedVersionGroups(selectedVersionGroups);
		this.setLessonPlanVersions(
			formatLessonPlanVersion(lessonPlanVersions, versionGroups).sort(sortLessonPlanVersions)
		);

		const selectedVersionGroup: VersionGroupModel =
			selectedVersionGroups.length > 0 ? selectedVersionGroups[0] : ({} as VersionGroupModel);
		this.setVersionGroup(selectedVersionGroup);

		if (needSetLoading) {
			this.setLoading(false);
		}
	}

	@Action
	async saveLessonPlanVersionData(action: LessonPlanVersionActionStatus): Promise<boolean> {
		let result = true;
		this.setLoading(true);
		switch (action) {
			case LessonPlanVersionActionStatus.Edit:
				await this.lessonPlanVersionService
					.editLessonPlanVersion(this.currentLessonPlanVersion)
					.catch(({ error_code, error_description }) => {
						result = showErrorMessage({ error_code, error_description });
					});
				break;
			case LessonPlanVersionActionStatus.Duplicate:
				await this.lessonPlanVersionService
					.duplicate(this.currentLessonPlanVersion)
					.catch(({ error_code, error_description }) => {
						result = showErrorMessage({ error_code, error_description });
					});
				break;
			case LessonPlanVersionActionStatus.Add:
			default:
				await this.lessonPlanVersionService
					.createLessonPlanVersion({
						...this.currentLessonPlanVersion,
						versionGroupId: this.currentVersionGroup.id,
						versionGroupName: this.currentVersionGroup.name
					})
					.catch(({ error_code, error_description }) => {
						result = showErrorMessage({ error_code, error_description });
					});
				break;
		}
		this.refreshLessonPlanVersions(false);
		this.setLoading(false);
		return result;
	}

	@Action
	async changeLessonPlanVersionStatus(payload: {
		lessonPlanVersion: ILessonPlanVersionModel;
		newStatus: LessonPlanVersionStatus;
	}): Promise<void> {
		this.setLoading(true);
		await this.lessonPlanVersionService
			.ChangeLessonPlanVersionStatus(payload.lessonPlanVersion.id as string, payload.newStatus, true)
			.catch(showErrorMessage);
		this.refreshLessonPlanVersions(false);
		this.setLoading(false);
	}

	@Action
	async deleteLessonPlanVersion(payload: {
		lessonPlanVersion: ILessonPlanVersionModel;
		versionId: string;
	}): Promise<void> {
		this.setLoading(true);
		await this.lessonPlanVersionService.deleteLessonPlanVersion(payload.versionId, payload.lessonPlanVersion.versionGroupName as string);
		this.refreshLessonPlanVersions(false);
		this.setLoading(false);
	}

	@Mutation
	setLoading(loading: boolean): void {
		this.loading = loading;
	}
}
